/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogProps,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Field,
    Input,
    makeStyles,
    mergeClasses,
    Textarea,
} from '@fluentui/react-components';
import { AddCircle16Regular, TargetRegular } from '@fluentui/react-icons';
import {
    CaseEventTemplate,
    CaseMilestoneTemplate,
    TaskTemplate,
    useInitialValues,
} from '../../../data/types';
import { Column, Row } from '../../common';
import { useFormik } from 'formik';
import { useCommonStyles } from '../../common/Styles/commonStyles';
import { LinkButton } from '../../controls/LinkButton';
import { AddEventTemplateDialog } from '../../dialogs/AddEventTemplateDialog';
import EventTemplateTable from './EventTemplateTable';
import { TaskTemplateTable } from './TaskTemplateTable';
import { FormSection } from '../../common/FormSection';
import { AddTaskTemplateDialog } from '../../dialogs/AddTaskTemplateDialog';
import { useValidation } from '../../../data/types/validation';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        maxWidth: '800px',
        maxHeight: '700px',
    },
    body: {
        //border: '1px solid green',
        flex: '1',
    },
    field: {
        width: '100%',
    },
});

export type AddEditCaseMilestoneTemplateDialogProps = Partial<DialogProps> & {
    children?: JSX.Element;
    milestoneToEdit?: CaseMilestoneTemplate;
    open?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    onSubmit?: (msTemplate: CaseMilestoneTemplate) => void;
};
export const AddEditCaseMilestoneTemplateDialog = (
    props: AddEditCaseMilestoneTemplateDialogProps,
) => {
    const { children, milestoneToEdit, open = false, onOpenChange, onSubmit } = props;

    //Hooks
    const initValues = useInitialValues();
    const { validateCaseMilestoneTemplate } = useValidation();

    //State
    const [isOpen, setIsOpen] = useState<boolean>(open);

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();
    const fieldClasses = mergeClasses(styles.field, commonStyles.text.field);

    //Form
    const initTemplate = (): CaseMilestoneTemplate => {
        if (!!milestoneToEdit) {
            return {
                ...milestoneToEdit,
            };
        }
        return initValues.caseMilestoneTemplate();
    };
    const milestoneForm = useFormik<CaseMilestoneTemplate>({
        initialValues: initTemplate(),
        onSubmit: async (values, { resetForm }) => {
            const msTemplate = values;

            if (!!msTemplate.name) {
                !!onSubmit && (await onSubmit(msTemplate));

                resetForm({ values: initTemplate() });
            }
        },
        validationSchema: validateCaseMilestoneTemplate.schema,
    });

    //UI Events
    const onClickSubmit = (e: React.MouseEvent) => {
        e.stopPropagation();
        milestoneForm.submitForm();
    };
    const onClickCancel = (e: React.MouseEvent) => {
        e.stopPropagation();
        resetForm();
    };
    const onDialogOpenChange = (e: any, data: any) => {
        e.stopPropagation();
        onOpenChange ? onOpenChange(data.open) : setIsOpen(data.open);
    };
    const resetForm = () => {
        milestoneForm.resetForm({ values: initTemplate() });
    };

    //Event Template Logic
    const submitEventTemplate = async (evtTemplate: CaseEventTemplate) => {
        let events = [...(milestoneForm.values.events || [])];
        const index = events.findIndex((evt) => evt.id == evtTemplate.id);
        if (index >= 0) {
            events = [...events.slice(0, index), evtTemplate, ...events.slice(index + 1)];
        } else {
            events = [...events, evtTemplate];
        }

        milestoneForm.setFieldValue('events', events);
    };
    const removeEventTemplate = async (evtTemplate: CaseEventTemplate) => {
        const events = milestoneForm.values.events?.filter((x) => x.id != evtTemplate.id);

        milestoneForm.setFieldValue('events', events);
    };

    //Task Template Logic
    const submitTaskTemplate = async (taskTemplate: TaskTemplate) => {
        let tasks = [...(milestoneForm.values.tasks || [])];
        const index = tasks.findIndex((t) => t.id == taskTemplate.id);
        if (index >= 0) {
            tasks = [...tasks.slice(0, index), taskTemplate, ...tasks.slice(index + 1)];
        } else {
            tasks = [...tasks, taskTemplate];
        }

        milestoneForm.setFieldValue('tasks', tasks);
    };
    const removeTaskTemplate = async (taskTemplate: TaskTemplate) => {
        const tasks = milestoneForm.values.tasks?.filter((x) => x.id != taskTemplate.id);

        milestoneForm.setFieldValue('tasks', tasks);
    };

    //Effects
    useEffect(() => {
        if (!!isOpen && !!milestoneToEdit) {
            resetForm();
        }
    }, [isOpen, milestoneToEdit]);

    useEffect(() => {
        setIsOpen(open);
    }, [open]);

    return (
        <>
            <Dialog modalType='alert' open={isOpen} onOpenChange={onDialogOpenChange}>
                <DialogTrigger disableButtonEnhancement>{children}</DialogTrigger>
                <DialogSurface className={styles.root}>
                    <DialogBody className={styles.body}>
                        <DialogTitle>
                            <Row gap='medium'>
                                <Column width='auto' vAlign='middle'>
                                    {<TargetRegular />}
                                </Column>
                                <Column>Create/Edit Milestone</Column>
                            </Row>
                        </DialogTitle>
                        <DialogContent style={{ maxHeight: '40rem' }}>
                            <Column noGap flex='1' scroll>
                                <Column gap='medium' padding='pb-3'>
                                    <Row>
                                        <Column gap='medium'>
                                            <Field
                                                label='Milestone Name'
                                                className={fieldClasses}
                                                required
                                                validationMessage={milestoneForm.errors.name}
                                            >
                                                <Input
                                                    name='name'
                                                    value={milestoneForm.values.name}
                                                    onChange={milestoneForm.handleChange}
                                                    maxLength={
                                                        validateCaseMilestoneTemplate.name.maxLength
                                                    }
                                                    data-testid='milestone-template-name-input'
                                                />
                                            </Field>
                                            <Field
                                                className={fieldClasses}
                                                label='Due Days from Created'
                                            >
                                                <Input
                                                    name='dueDaysFromCreated'
                                                    value={`${milestoneForm.values.dueDaysFromCreated ?? ''}`}
                                                    onChange={milestoneForm.handleChange}
                                                    placeholder='0'
                                                    data-testid='milestone-template-due-days-input'
                                                />
                                            </Field>
                                        </Column>
                                        <Column gap='medium'>
                                            <Field
                                                label='Description'
                                                className={fieldClasses}
                                                validationMessage={milestoneForm.errors.description}
                                                hint={`${milestoneForm.values.description?.length ?? 0} / ${validateCaseMilestoneTemplate.description.maxLength}`}
                                            >
                                                <Textarea
                                                    name='description'
                                                    value={milestoneForm.values.description}
                                                    onChange={milestoneForm.handleChange}
                                                    rows={2}
                                                    data-testid='milestone-template-description-input'
                                                    maxLength={
                                                        validateCaseMilestoneTemplate.description
                                                            .maxLength
                                                    }
                                                />
                                            </Field>
                                            <Field
                                                label='Deliverables'
                                                className={fieldClasses}
                                                validationMessage={
                                                    milestoneForm.errors.deliverables
                                                }
                                                hint={`${milestoneForm.values.deliverables?.length ?? 0} / ${validateCaseMilestoneTemplate.deliverables.maxLength}`}
                                            >
                                                <Textarea
                                                    name='deliverables'
                                                    value={milestoneForm.values.deliverables}
                                                    onChange={milestoneForm.handleChange}
                                                    rows={3}
                                                    data-testid='milestone-template-deliverables-input'
                                                    maxLength={
                                                        validateCaseMilestoneTemplate.deliverables
                                                            .maxLength
                                                    }
                                                />
                                            </Field>
                                        </Column>
                                    </Row>
                                </Column>

                                <Column gap='medium' padding='pb-3'>
                                    <FormSection
                                        title='Events'
                                        action={
                                            <AddEventTemplateDialog onSubmit={submitEventTemplate}>
                                                <LinkButton
                                                    text='Add Event'
                                                    icon={<AddCircle16Regular />}
                                                    data-testid='milestone-template-add-event-button'
                                                />
                                            </AddEventTemplateDialog>
                                        }
                                    >
                                        <EventTemplateTable
                                            eventTemplates={milestoneForm.values.events}
                                            onSubmit={submitEventTemplate}
                                            onDelete={removeEventTemplate}
                                        />
                                    </FormSection>

                                    <FormSection
                                        title='Tasks'
                                        action={
                                            <AddTaskTemplateDialog onSubmit={submitTaskTemplate}>
                                                <LinkButton
                                                    text='Add Task'
                                                    icon={<AddCircle16Regular />}
                                                    data-testid='milestone-template-add-task-button'
                                                />
                                            </AddTaskTemplateDialog>
                                        }
                                    >
                                        <TaskTemplateTable
                                            taskTemplates={milestoneForm.values.tasks}
                                            onSubmit={submitTaskTemplate}
                                            onDelete={removeTaskTemplate}
                                        />
                                    </FormSection>
                                </Column>
                            </Column>
                        </DialogContent>
                        <DialogActions>
                            <DialogTrigger disableButtonEnhancement>
                                <Button
                                    className={commonStyles.button.root}
                                    type='submit'
                                    appearance='primary'
                                    disabled={!milestoneForm.dirty}
                                    onClick={onClickSubmit}
                                    data-testid='milestone-template-submit-button'
                                >
                                    Submit
                                </Button>
                            </DialogTrigger>
                            <DialogTrigger disableButtonEnhancement>
                                <Button
                                    className={commonStyles.button.root}
                                    appearance='secondary'
                                    onClick={onClickCancel}
                                    data-testid='milestone-template-cancel-button'
                                >
                                    Cancel
                                </Button>
                            </DialogTrigger>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </>
    );
};
